import { gql } from '@/__generated__/gql';

export const COMMITMENT_PLANS_QUERY = gql(`
  query InvestorDetailsPacingTabCommitmentPlans($input: GetCommitmentPlansInput!) {
    commitmentPlans(input: $input) {
      total
      limit
      offset
      data {
        id
        name
        startDate
        startingPortfolioValue
        drawdownTarget
        startingAllocation
        growthRate
        active
        autoPlanStatus
      }
    }
  }
`);

export const COMMITMENT_PLAN_QUERY = gql(`
  query InvestorDetailsPacingTabCommitmentPlan($id: String!) {
    commitmentPlan(id: $id) {
      id
      name
      startDate
      startingPortfolioValue
      drawdownTarget
      startingAllocation
      growthRate
      active
      autoPlanStatus
      assetClassDrawdownTargets {
        actuals {
          name
          portfolioPercentage
          privateCapitalPercentage
          subAssetClassDrawdownTargets {
            name
            portfolioPercentage
            privateCapitalPercentage
          }
        }
        targets {
          name
          portfolioPercentage
          privateCapitalPercentage
          subAssetClassDrawdownTargets {
            name
            portfolioPercentage
            privateCapitalPercentage
          }
        }
      }
      planAllocations {
        startingDate
        endingDate
        assetClassAllocations {
          assetClassId
          name
          allocation
          subAssetClasses {
            subAssetClassId
            name
            allocation
          }
        }
      }
      forecastSummary {
        assetClassForecasts {
          name
          target
          asOfForecast
          fiveYearForecast
          tenYearForecast
          subAssetClassForecasts {
            name
            target
            asOfForecast
            fiveYearForecast
            tenYearForecast
          }
        }
        fiveYearDate
        tenYearDate
      }
      drawdownTargets {
        actuals {
          drawdown {
            id
            name
            portfolioPercentage
            privateCapitalPercentage
            subAssetClassDrawdownTargets {
              id
              name
              portfolioPercentage
              privateCapitalPercentage
            }
          }
          evergreen {
            id
            name
            portfolioPercentage
            privateCapitalPercentage
            subAssetClassDrawdownTargets {
              id
              name
              portfolioPercentage
              privateCapitalPercentage
            }
          }
        }
        targets {
          drawdown {
            id
            name
            portfolioPercentage
            privateCapitalPercentage
            subAssetClassDrawdownTargets {
              id
              name
              portfolioPercentage
              privateCapitalPercentage
            }
          }
          evergreen {
            id
            name
            portfolioPercentage
            privateCapitalPercentage
            subAssetClassDrawdownTargets {
              id
              name
              portfolioPercentage
              privateCapitalPercentage
            }
          }
        }
      }
      forecast {
        netContributions {
          date
          value
          actual
        }
        netDistributions {
          date
          value
          actual
        }
        netCommitments {
          date
          value
          actual
        }
        uncalledCapital {
          date
          value
          actual
        }
        totalNAVs {
          date
          value
          actual
        }
        cumulativeContributions {
          date
          value
          actual
        }
        cumulativeDistributions {
          date
          value
          actual
        }
        existingCommitmentNAVs {
          investmentId
          name
          data {
            date
            value
            actual
          }
        }
        existingCommitmentDistributions {
          investmentId
          name
          data {
            date
            value
            actual
          }
        }
        existingCommitmentContributions {
          investmentId
          name
          data {
            date
            value
            actual
          }
        }
        assetClassContributions {
          name
          date
          value
          actual
          subAssetClasses {
            name
            date
            value
            actual
          }
        }
        assetClassDistributions {
          name
          date
          value
          actual
          subAssetClasses {
            name
            date
            value
            actual
          }
        }
        assetClassNAVs {
          name
          date
          value
          actual
          subAssetClasses {
            name
            date
            value
            actual
          }
        }
        assetClassPercentages {
          name
          date
          value
          actual
          subAssetClasses {
            name
            date
            value
            actual
          }
        }
      }
    }
  }
`);

export const PACING_TAB_AUTO_PLAN_STATUS_QUERY = gql(`
  query InvestorDetailsPacingTabAutoPlanStatusCommitmentPlan($id: String!) {
    commitmentPlan(id: $id) {
      autoPlanStatus
    }
  }
`);

export const FORECAST_SUMMARY_QUERY = gql(`
  query InvestorDetailsPacingTabForecastSummary(
    $legalEntityId: String!
    $allocations: [CreateAllocationsByDateDto!]!
    $startDate: DateTime!
    $growthRate: Float!
  ) {
    forecastSummary(legalEntityId: $legalEntityId, allocations: $allocations, startDate: $startDate, growthRate: $growthRate) {
      assetClassForecasts {
        name
        target
        asOfForecast
        fiveYearForecast
        tenYearForecast
        subAssetClassForecasts {
          name
          target
          asOfForecast
          fiveYearForecast
          tenYearForecast
        }
      }
    }
  }
`);

export const DELETE_COMMITMENT_PLAN_DIALOG_COMMITMENT_PLANS_QUERY = gql(`
  query InvestorDetailsPacingTabDeleteCommitmentPlanDialogCommitmentPlans($input: GetCommitmentPlansInput!) {
    commitmentPlans(input: $input) {
      total
      data {
        id
        name
        active
      }
    }
  }
`);

export const PACING_TAB_AUTO_PLAN_MUTATION = gql(`
  mutation InvestorDetailsPacingTabAutoPlan($input: AutoPlanDto!) {
    autoPlan(input: $input) {
      startingPortfolioValue
      drawdownTarget
      startingAllocation
      assetClassDrawdownTargets {
        actuals {
          name
          portfolioPercentage
          privateCapitalPercentage
          subAssetClassDrawdownTargets {
            name
            portfolioPercentage
            privateCapitalPercentage
          }
        }
        targets {
          name
          portfolioPercentage
          privateCapitalPercentage
          subAssetClassDrawdownTargets {
            name
            portfolioPercentage
            privateCapitalPercentage
          }
        }
      }
      drawdownTargets {
        actuals {
          drawdown {
            id
            name
            portfolioPercentage
            privateCapitalPercentage
            subAssetClassDrawdownTargets {
              id
              name
              portfolioPercentage
              privateCapitalPercentage
            }
          }
          evergreen {
            id
            name
            portfolioPercentage
            privateCapitalPercentage
            subAssetClassDrawdownTargets {
              id
              name
              portfolioPercentage
              privateCapitalPercentage
            }
          }
        }
        targets {
          drawdown {
            id
            name
            portfolioPercentage
            privateCapitalPercentage
            subAssetClassDrawdownTargets {
              id
              name
              portfolioPercentage
              privateCapitalPercentage
            }
          }
          evergreen {
            id
            name
            portfolioPercentage
            privateCapitalPercentage
            subAssetClassDrawdownTargets {
              id
              name
              portfolioPercentage
              privateCapitalPercentage
            }
          }
        }
      }
    }
  }
`);

export const AUTO_PLAN_MUTATION = gql(`
  mutation InvestorDetailsPacingTabAutoPlanWizardModalPageAutoPlan($input: AutoPlanDto!) {
    autoPlan(input: $input) {
      id
      name
      startDate
      startingPortfolioValue
      drawdownTarget
      startingAllocation
      growthRate
      active
     autoPlanStatus
      forecastSummary {
        assetClassForecasts {
          name
          target
          asOfForecast
          fiveYearForecast
          tenYearForecast
          subAssetClassForecasts {
            name
            target
            asOfForecast
            fiveYearForecast
            tenYearForecast
          }
        }
        fiveYearDate
        tenYearDate
      }
      drawdownTargets {
        actuals {
          drawdown {
            id
            name
            portfolioPercentage
            privateCapitalPercentage
            subAssetClassDrawdownTargets {
              id
              name
              portfolioPercentage
              privateCapitalPercentage
            }
          }
          evergreen {
            id
            name
            portfolioPercentage
            privateCapitalPercentage
            subAssetClassDrawdownTargets {
              id
              name
              portfolioPercentage
              privateCapitalPercentage
            }
          }
        }
        targets {
          drawdown {
            id
            name
            portfolioPercentage
            privateCapitalPercentage
            subAssetClassDrawdownTargets {
              id
              name
              portfolioPercentage
              privateCapitalPercentage
            }
          }
          evergreen {
            id
            name
            portfolioPercentage
            privateCapitalPercentage
            subAssetClassDrawdownTargets {
              id
              name
              portfolioPercentage
              privateCapitalPercentage
            }
          }
        }
      }
      planAllocations {
        startingDate
        endingDate
        assetClassAllocations {
          assetClassId
          name
          allocation
          subAssetClasses {
            subAssetClassId
            name
            allocation
          }
        }
      }
    }
  }
`);

export const CREATE_COMMITMENT_PLAN_MUTATION = gql(`
  mutation InvestorDetailsPacingTabCreateCommitmentPlan($input: CreateCommitmentPlanDto!) {
    createCommitmentPlan(input: $input) {
      id
      name
      startDate
      startingPortfolioValue
      drawdownTarget
      startingAllocation
      growthRate
      active
      planAllocations {
        startingDate
        endingDate
        assetClassAllocations {
          assetClassId
          name
          allocation
          subAssetClasses {
            subAssetClassId
            name
            allocation
          }
        }
      }
    }
  }
`);

export const UPDATE_COMMITMENT_PLAN_MUTATION = gql(`
  mutation InvestorDetailsPacingTabUpdateCommitmentPlan($input: UpdateCommitmentPlanInput!) {
    updateCommitmentPlan(input: $input) {
      id
      name
      startDate
      startingPortfolioValue
      drawdownTarget
      startingAllocation
      growthRate
      active
      planAllocations {
        startingDate
        endingDate
        assetClassAllocations {
          assetClassId
          name
          allocation
          subAssetClasses {
            subAssetClassId
            name
            allocation
          }
        }
      }
    }
  }
`);

export const DELETE_COMMITMENT_PLAN_MUTATION = gql(`
  mutation InvestorDetailsPacingTabDeleteCommitmentPlan($id: String!) {
    deleteCommitmentPlan(id: $id)
  }
`);
